import { type PlaybackController } from 'components/VideoPlayer/controller/PlaybackController';
import { type CameraIdentifier } from 'components/VideoPlayer/controller/PlayingCamerasController';
import { useControllerState } from 'solox';
import isShallowEqual from 'utils/isShallowEqual';

export default function usePlayingCamsPlaylistStartAndEnd(
  playingCameraIds: readonly CameraIdentifier[],
  playbackController: PlaybackController,
) {
  const { start, end } = useControllerState(
    playbackController.state,
    (state) => {
      // Get the lowest start and highest end
      const { lowestStart, highestEnd } = playingCameraIds.reduce(
        (acc, cameraId) => {
          const cameraState = state.byPlayerId?.[cameraId?.playerId]?.state;
          const playlist = cameraState?.playlist;

          if (playlist?.state === 'loaded') {
            const firstSegment = playlist.segments?.[0];
            const lastSegment = playlist.segments?.[playlist.segments.length - 1];

            // Update lowest start
            if (firstSegment?.start !== undefined) {
              acc.lowestStart =
                acc.lowestStart !== undefined
                  ? Math.min(acc.lowestStart, firstSegment.start)
                  : firstSegment.start;
            }

            // Update highest end
            if (lastSegment?.end !== undefined) {
              acc.highestEnd =
                acc.highestEnd !== undefined
                  ? Math.max(acc.highestEnd, lastSegment.end)
                  : lastSegment.end;
            }
          }

          return acc;
        },
        { lowestStart: undefined, highestEnd: undefined } as {
          lowestStart: number | undefined;
          highestEnd: number | undefined;
        },
      );

      return {
        isPlayingLive: playbackController.isPlayingLive(),
        start: lowestStart,
        end: highestEnd,
        desiredTime: state.desiredTime,
      };
    },
    isShallowEqual,
  );

  return { start, end };
}
